import { useState } from "react";
import { Building2, ChevronRight, Info, Loader2 } from "lucide-react";

import { fmt } from "@/hooks/useREInvestingSim";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  onComplete: () => Promise<void> | void;
}

function TipLabel({ label, tip }: { label: string; tip: string }) {
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3 w-3 cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

function calcMonthlyPI(loan: number, rate: number, years: number) {
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return Math.round(loan / n);
  return Math.round((loan * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1));
}

export default function TutorialDeal({ onComplete }: Props) {
  const price = 180000;
  const baseRent = 1650;
  const taxMonthly = 225;
  const insuranceMonthly = 100;

  const [dpPct, setDpPct] = useState(20);
  const [rentEstimate, setRentEstimate] = useState(baseRent);
  const [repairReserve, setRepairReserve] = useState(5);
  const [interestRate, setInterestRate] = useState(6.5);
  const [continuing, setContinuing] = useState(false);
  const [popupText, setPopupText] = useState<{ title: string; body: string } | null>(null);

  const downPayment = Math.round((price * dpPct) / 100);
  const closingCosts = Math.round(price * 0.03);
  const totalCashNeeded = downPayment + closingCosts;
  const loanAmount = price - downPayment;
  const monthlyMortgage = calcMonthlyPI(loanAmount, interestRate / 100, 30);
  const maintenanceReserve = Math.round((rentEstimate * repairReserve) / 100);
  const vacancyReserve = Math.round(rentEstimate * 0.05);
  const totalExpenses = monthlyMortgage + taxMonthly + insuranceMonthly + maintenanceReserve + vacancyReserve;
  const monthlyCashFlow = rentEstimate - totalExpenses;
  const annualCashFlow = monthlyCashFlow * 12;
  const cashOnCash = totalCashNeeded > 0 ? ((annualCashFlow / totalCashNeeded) * 100).toFixed(1) : "0";

  const handleContinue = async () => {
    if (continuing) return;

    setContinuing(true);
    try {
      await onComplete();
    } finally {
      setContinuing(false);
    }
  };

  return (
    <div className="space-y-6">
      {popupText && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="glass-card glow-border w-full max-w-sm space-y-4 p-6 animate-scale-in">
            <h3 className="font-heading font-bold text-foreground">{popupText.title}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{popupText.body}</p>
            <Button onClick={() => setPopupText(null)} className="w-full">
              Got it
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2 text-center">
        <h2 className="font-heading text-lg font-bold text-foreground">Guided Tutorial Deal</h2>
        <p className="text-sm text-muted-foreground">
          Walk through a sample deal, then continue straight into your first live investment screen.
        </p>
      </div>

      <div className="glass-card glow-border space-y-3 rounded-xl p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-heading font-bold text-foreground">Maple Grove Duplex</p>
            <p className="text-xs text-muted-foreground">Stonegate • Duplex • Fair condition</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-muted/30 p-2">
            <p className="text-[10px] text-muted-foreground">Price</p>
            <p className="font-heading text-sm font-bold text-foreground">{fmt(price)}</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-2">
            <p className="text-[10px] text-muted-foreground">Est. Rent</p>
            <p className="font-heading text-sm font-bold text-foreground">{fmt(baseRent)}/mo</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-2">
            <p className="text-[10px] text-muted-foreground">Risk</p>
            <p className="font-heading text-sm font-bold text-primary">Moderate</p>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <TipLabel
              label="Down Payment %"
              tip="The down payment is the cash you pay upfront. A larger down payment lowers the loan amount, but uses more of your capital."
            />
            <button
              onClick={() =>
                setPopupText({
                  title: "Down Payment",
                  body:
                    "The down payment is the cash you pay upfront. A larger down payment lowers the loan amount, but uses more of your capital. Investment properties typically require 15–25% down.",
                })
              }
              className="text-xs text-primary underline"
            >
              Learn more
            </button>
          </div>
          <Slider value={[dpPct]} onValueChange={(value) => setDpPct(value[0])} min={3} max={50} step={1} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{dpPct}%</span>
            <span>{fmt(downPayment)}</span>
          </div>
        </div>

        <div className="space-y-2">
          <TipLabel
            label="Rent Estimate"
            tip="Be careful not to assume perfect rent. Conservative estimates help protect investors from disappointment."
          />
          <Slider value={[rentEstimate]} onValueChange={(value) => setRentEstimate(value[0])} min={1200} max={2100} step={50} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{fmt(rentEstimate)}/mo</span>
          </div>
        </div>

        <div className="space-y-2">
          <TipLabel
            label="Maintenance Reserve %"
            tip="Repairs are real ownership costs. Forgetting them can make a property look more profitable than it really is."
          />
          <Slider value={[repairReserve]} onValueChange={(value) => setRepairReserve(value[0])} min={0} max={15} step={1} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{repairReserve}%</span>
            <span>{fmt(maintenanceReserve)}/mo</span>
          </div>
        </div>

        <div className="space-y-2">
          <TipLabel
            label="Interest Rate"
            tip="Interest rates affect your monthly payment and total cost of borrowing. Even small rate changes can significantly impact cash flow."
          />
          <Slider value={[interestRate * 10]} onValueChange={(value) => setInterestRate(value[0] / 10)} min={40} max={90} step={1} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{interestRate.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      <div className="glass-card space-y-3 rounded-xl p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground">Deal Analysis</h3>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Down Payment", value: fmt(downPayment), tip: "Cash paid upfront" },
            { label: "Closing Costs", value: fmt(closingCosts), tip: "Purchase expenses like lender, title, and transaction fees." },
            { label: "Total Cash Needed", value: fmt(totalCashNeeded), tip: "Down payment plus closing costs." },
            { label: "Loan Amount", value: fmt(loanAmount), tip: "Amount borrowed from the lender." },
            { label: "Monthly Mortgage", value: fmt(monthlyMortgage), tip: "Principal and interest payment." },
            { label: "Monthly Rent", value: fmt(rentEstimate), tip: "Expected monthly rental income." },
            { label: "Total Expenses", value: fmt(totalExpenses), tip: "Mortgage, taxes, insurance, and reserves." },
            { label: "Vacancy Reserve", value: fmt(vacancyReserve), tip: "A buffer for months without rent." },
          ].map((item) => (
            <div key={item.label} className="rounded-lg bg-muted/30 p-2">
              <TipLabel label={item.label} tip={item.tip} />
              <p className="mt-0.5 font-heading text-sm font-bold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 border-t border-border pt-3">
          <div className="rounded-lg bg-primary/10 p-3 text-center">
            <p className="text-[10px] text-muted-foreground">Monthly Cash Flow</p>
            <p className="font-heading text-sm font-bold text-primary">{fmt(monthlyCashFlow)}</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-3 text-center">
            <p className="text-[10px] text-muted-foreground">Annual Cash Flow</p>
            <p className="font-heading text-sm font-bold text-foreground">{fmt(annualCashFlow)}</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-3 text-center">
            <p className="text-[10px] text-muted-foreground">Cash-on-Cash</p>
            <p className="font-heading text-sm font-bold text-foreground">{cashOnCash}%</p>
          </div>
        </div>
      </div>

      <div className="glass-card space-y-2 rounded-xl p-4">
        <p className="font-heading text-xs font-semibold text-foreground">Key Concepts</p>
        <div className="space-y-1.5 text-xs text-muted-foreground">
          <p>• Cash flow is what remains after rent comes in and expenses go out.</p>
          <p>• Leverage lets you control a larger asset with less cash upfront.</p>
          <p>• Cash-on-cash return compares annual income to the cash you invested.</p>
        </div>
      </div>

      <Button onClick={handleContinue} size="lg" className="w-full gap-2" disabled={continuing}>
        {continuing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading first deal...
          </>
        ) : (
          <>
            I understand — let me invest
            <ChevronRight className="h-5 w-5" />
          </>
        )}
      </Button>
    </div>
  );
}
